import React, { useState, useRef, useEffect } from "react";
import { collection, onSnapshot, query, where, doc, updateDoc, addDoc, serverTimestamp, runTransaction } from "firebase/firestore";
import { db } from "./firebase";
import { buildFullName } from "./useChat";
import { color, font, type, space, radius, shadow, ease } from "./theme";

import logo from "../icons/ojtern.png";
import dashboardIcon      from "../icons/dashboard.png";
import userIcon from "../icons/user.png";
import postOJTIcon        from "../icons/post.png";
import applicantsIcon     from "../icons/applicants.png";
import messagesIcon       from "../icons/messages.png";
import accountProfileIcon from "../icons/accountprofile.png";
import aboutIcon          from "../icons/about.png";
import downloadIcon       from "../icons/download.png";
import pdfIcon            from "../icons/pdf.png";
import imgIcon            from "../icons/img.png";
import viewIcon           from "../icons/view.png";
import reportIcon         from "../icons/report.png";
import { standingOf, STANDING_TONE } from "./accountStanding";

// ── Design tokens, aliased for this screen ────────────────────────────────────
// Same aliases as CoordinatorStudentListScreen so the two screens stay in sync.
const ink        = color.ink;
const inkBody    = color.inkBody;
const inkMuted   = color.inkMuted;
const inkFaint   = color.inkFaint;
const surface    = color.wine600;      // rows, cards
const page       = color.wine900;      // page background
const line       = color.wine700;      // hairlines & borders
const lineSoft   = color.wine800;
const panel      = color.blush100;     // dark header bar
const panelDeep  = color.blush50;
const onPanel    = color.onWine;
const onPanelDim = color.onWineMuted;

// ── Report support ────────────────────────────────────────────────────────────
// Same Cloudinary target as StudentFindCompanyScreen so every report's evidence
// lands in one folder regardless of who filed it.
const CLOUDINARY_CLOUD_NAME    = "doalndt5l";
const CLOUDINARY_UPLOAD_PRESET = "ojtern_docs";

// Concerns a company can raise about an applicant. Same categories as every other report form (student → company in Find Company,
// company → student from chat), so a coordinator sees one consistent set of
// concerns whoever filed the report.
const reportCategories = [
  { label: "Fraud and Scam", description: "Job scams are fraudulent schemes where scammers impersonate employers to steal money, personal information, or coerce victims into fake work activities.", details: ["Fake job postings requiring payment", "Identity theft", "Misrepresentation of company"] },
  { label: "Discrimination", description: "Discrimination involves unfair treatment based on race, gender, age, religion, disability, or other protected characteristics.", details: ["Racial discrimination", "Gender-based bias", "Age discrimination", "Religious intolerance"] },
  { label: "Sexual Harassment", description: "Sexual harassment includes any unwelcome sexual advances or other verbal or physical conduct of a sexual nature.", details: ["Unwanted physical contact", "Verbal harassment", "Hostile work environment", "Quid pro quo harassment"] },
  { label: "Harmful Misinformation", description: "Spreading false information about OJT programs, company practices, or student requirements.", details: ["False program descriptions", "Fake requirements", "Misleading slot information"] },
  { label: "Workplace Misconduct", description: "Behavior that violates company policies or professional standards, including unsafe working conditions.", details: ["Unsafe working conditions", "Violation of OJT agreement", "Forced overtime", "Unpaid work"] },
  { label: "Others", description: "Any other concern not listed above. Please provide a detailed description.", details: [] },
];

// A company may only ever report a student. Coordinators are school staff and
// are never reportable from the company side, so anything that isn't clearly a
// student record gets no Report button at all. Records with no role field are
// treated as students because every document in `applications` is a student's.
const isReportableApplicant = (person) => {
  const role = String(person?.role || person?.userType || "student").toLowerCase();
  return role === "student";
};

// ── College → Program → Specialization data ────────────────────────────────
const COLLEGE_DATA = {
  "College of Computer Studies": {
    programs: {
      "Bachelor of Science in Information Technology": { specializations: [] },
    },
  },
  "College of Business and Accountancy": {
    programs: {
      "BS Business Administration — Major in Marketing Management": { specializations: [] },
      "Bachelor of Science in Accountancy": { specializations: [] },
    },
  },
  "College of Criminal Justice Education": {
    programs: {
      "Bachelor of Science in Criminology": { specializations: [] },
    },
  },
  "College of Liberal Arts": {
    programs: {
      "Bachelor of Arts in Political Science": { specializations: [] },
    },
  },
  "College of Education": {
    programs: {
      "Bachelor of Elementary Education": { specializations: [] },
      "BS Education — Major in English": { specializations: [] },
      "BS Education — Major in Mathematics": { specializations: [] },
    },
  },
  "College of Hospitality and Tourism Management": {
    programs: {
      "Bachelor of Science in Tourism Management": { specializations: [] },
      "Bachelor of Science in Hospitality Management": { specializations: [] },
    },
  },
};

// ── Status options & colors (kept in sync with CoordinatorStudentListScreen) ──
const STATUS_OPTIONS = ["Accepted", "Declined", "Pending", "In Review", "To Interview"];

const STATUS_COLORS = {
  "Accepted":     { bg: "#358D5E", color: color.white },
  "Declined":     { bg: "#FF0000", color: color.white },
  "Pending":      { bg: "#CCC929", color: ink },
  "In Review":    { bg: "#353A8D", color: color.white },
  "To Interview": { bg: "#7C2889", color: color.white },
  // Set by the STUDENT (they cancelled the application). Never selectable
  // here, so it's not in STATUS_OPTIONS — the company can only view it.
  "Withdrawn":    { bg: "#6B6B6B", color: color.white },
};

// Statuses after which the company can no longer change anything.
const LOCKED_STATUSES = ["Accepted", "Declined", "Withdrawn"];

// Forward-only progression order. "Declined" isn't part of the sequence since
// an applicant can be declined from any non-final stage.
const STATUS_RANK = { "Pending": 0, "In Review": 1, "To Interview": 2, "Accepted": 3 };

// An applicant can only move forward through the sequence — never back to an
// earlier status. On top of that, the two final decisions (Accepted/Declined)
// are only unlocked once the applicant has reached "To Interview", so nobody
// can be accepted or declined straight out of Pending or In Review.
// Accepted/Declined themselves are terminal and handled via the `locked` prop.
const getDisabledStatusOptions = (currentStatus) => {
  if (!(currentStatus in STATUS_RANK)) return [];
  const disabled = STATUS_OPTIONS.filter(
    opt => opt in STATUS_RANK && STATUS_RANK[opt] < STATUS_RANK[currentStatus]
  );
  if (STATUS_RANK[currentStatus] < STATUS_RANK["To Interview"]) {
    for (const finalOpt of ["Accepted", "Declined"]) {
      if (!disabled.includes(finalOpt)) disabled.push(finalOpt);
    }
  }
  return disabled;
};

const DROPDOWN_ITEM_HEIGHT = 36;
const DROPDOWN_PADDING = 16;
const DROPDOWN_HEIGHT = STATUS_OPTIONS.length * DROPDOWN_ITEM_HEIGHT + DROPDOWN_PADDING;

// TODO: Populate from backend
const SEX_OPTIONS = [];

// ── Shared chip style: one look for every selectable pill in this screen ──────
const chip = (on) => ({
  padding: "5px 12px",
  borderRadius: radius.pill,
  fontFamily: font.ui,
  ...type.helper,
  cursor: "pointer",
  userSelect: "none",
  background: on ? ink : color.wine800,
  color: on ? color.white : inkBody,
  border: `1px solid ${on ? ink : line}`,
  transition: `all 160ms ${ease}`,
});

// ── Responsive styles ─────────────────────────────────────────────────────────
// Page shape mirrors CoordinatorStudentListScreen (padded scroll area →
// floating dark bar → chips → full-width rows).
const ResponsiveStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Monomaniac+One&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-thumb { background: ${color.wine400}; border-radius: 999px; }
    ::-webkit-scrollbar-track { background: transparent; }
    .app-msg-scroll::-webkit-scrollbar { width: 4px; }
    .app-msg-scroll::-webkit-scrollbar-thumb { background: ${color.wine400}; border-radius: 4px; }
    .app-msg-scroll::-webkit-scrollbar-track { background: transparent; }

    /* List wrapper: does NOT scroll itself — header + chips stay fixed, only the rows box scrolls */
    .ca-list-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      width: 100%;
      background: ${page};
      padding: clamp(16px, 4vw, 28px) clamp(16px, 4vw, 32px);
    }

    /* Floating dark header bar */
    .ca-search-bar {
      background: ${panel};
      border-radius: ${radius.panel};
      padding: 18px 22px;
      margin-bottom: ${space.md};
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${space.md};
      flex-wrap: nowrap;
    }
    @media (max-width: 480px) {
      .ca-search-bar { padding: 14px; gap: 10px; }
    }

    .ca-search-input { width: 170px; }
    .ca-search-input::placeholder { color: ${inkFaint}; }
    @media (max-width: 480px) {
      .ca-search-input { width: 90px; }
    }
    @media (max-width: 380px) {
      .ca-search-input { width: 62px; }
    }

    /* Full-width applicant rows */
    .ca-rows {
      display: flex;
      flex-direction: column;
      gap: ${space.sm};
    }
    .ca-row {
      background: ${surface};
      border: 1px solid ${line};
      border-radius: ${radius.pill};
      box-shadow: ${shadow.input};
      padding: 10px 20px 10px 18px;
      display: flex;
      align-items: center;
      gap: 14px;
      cursor: pointer;
      transition: border-color 200ms ${ease}, box-shadow 200ms ${ease};
    }
    .ca-row:hover {
      border-color: ${color.wine400};
      box-shadow: 0 8px 22px rgba(10,10,10,0.08);
    }
    .ca-row-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 3px;
      overflow: hidden;
    }
    /* Hide program on very small screens */
    .ca-row-program { display: inline; }
    @media (max-width: 400px) {
      .ca-row-program { display: none; }
    }
    @media (max-width: 560px) {
      .ca-row { padding: 10px 14px 10px 14px; gap: 10px; }
    }

    .ca-list-wrapper :focus-visible,
    .ca-modal-inner :focus-visible {
      outline: none;
      box-shadow: ${shadow.focus};
      border-radius: ${radius.pill};
    }

    /* ── Personal Details Modal — sized to match CoordinatorReportCompanyScreen's report modal ── */
    .ca-modal-inner {
      background: ${surface};
      border: 1px solid ${line};
      border-radius: 18px;
      width: 660px;
      max-width: calc(100vw - 32px);
      max-height: 80vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: 0 20px 50px rgba(0,0,0,0.25);
    }
    @media (max-width: 560px) {
      .ca-modal-inner {
        width: calc(100vw - 72px);
        max-height: 60vh;
        border-radius: 14px;
      }
    }

    .ca-modal-header {
      background: ${surface};
      border-bottom: 1px solid ${line};
      min-height: 60px;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-shrink: 0;
    }
    @media (max-width: 560px) {
      .ca-modal-header { min-height: 48px; padding: 10px 14px; }
      .ca-modal-header h2 { font-size: 1.1rem !important; }
    }

    .ca-modal-body {
      overflow-y: auto;
      overflow-x: hidden;   /* ← idagdag */
      padding: 20px;
      flex: 1;
    }
    @media (max-width: 560px) {
      .ca-modal-body { padding: 14px; }
    }

    /* Name fields: wrap on small screens */
    .ca-name-row {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      margin-bottom: 14px;
      align-items: flex-end;
    }

    /* Contact row: wrap on small screens */
    .ca-contact-row {
      display: flex;
      gap: 36px;
      flex-wrap: wrap;
      margin-bottom: 14px;
    }

    /* Attached files: fixed-height scroll area so many files never push the footer around */
    .ca-files-scroll {
      max-height: 118px;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px 8px 2px 0;   /* room for the download badge that sticks out at the top-right */
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-content: flex-start;
    }
    .ca-files-scroll::-webkit-scrollbar { width: 6px; }
    .ca-files-scroll::-webkit-scrollbar-thumb { background: ${line}; border-radius: 3px; }

    /* Footer: pinned below the scrolling body (Status + Message + Report) */
    .ca-modal-footer {
      flex-shrink: 0;
      border-top: 1px solid ${line};
      background: ${surface};
      padding: 12px 20px 14px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 10px;
    }
    @media (max-width: 560px) {
      .ca-modal-footer { padding: 10px 14px 12px; gap: 8px; }
    }
    .ca-footer-note {
      max-height: 56px;
      overflow-y: auto;
      text-align: right;
    }

    /* ── Status description popup ── */
    .ca-popup-inner {
      background: ${surface};
      border: 1px solid ${line};
      border-radius: ${radius.panel};
      width: 100%;
      max-width: 600px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      box-shadow: ${shadow.panel};
    }

    .ca-popup-body {
      padding: 16px 28px 0;
    }
    @media (max-width: 480px) {
      .ca-popup-body { padding: 12px 16px 0; }
    }

    .ca-popup-footer {
      background: ${panel};
      padding: 14px 24px;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 8px;
    }
    @media (max-width: 480px) {
      .ca-popup-footer { padding: 12px 16px; }
    }

    /* ── Standalone layout (non-embedded) ── */

    /* App header */
    .ca-app-header {
      height: 70px;
      flex-shrink: 0;
      background: ${panel};
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 24px;
      border-bottom: 1px solid ${color.blush300};
    }
    @media (max-width: 560px) {
      .ca-app-header { padding: 0 14px; height: 58px; }
    }

    /* Sidebar: hidden on mobile */
    .ca-sidebar {
      width: 260px;
      flex-shrink: 0;
      background: ${panel};
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      border-right: 1px solid ${color.blush300};
    }
    @media (max-width: 700px) {
      .ca-sidebar { display: none; }
    }

    .ca-nav-label { display: inline; }

    @media (prefers-reduced-motion: reduce) {
      .ca-row { transition: none; }
    }
  `}</style>
);

const Chip = ({ label }) => (
  <span style={{
    display: "inline-block", padding: "5px 14px", borderRadius: radius.pill,
    background: color.wine800, border: `1px solid ${line}`,
    fontFamily: font.ui, ...type.helper, color: inkBody, whiteSpace: "nowrap",
  }}>
    {label}
  </span>
);

const FieldLabel = ({ children, style }) => (
  <p style={{
    fontFamily: font.ui, ...type.label, color: ink, marginBottom: "6px", ...style,
  }}>
    {children}
  </p>
);

const StudentAvatar = ({ size = 42 }) => (
  <img
    src={userIcon}
    alt=""
    style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
  />
);

// ── StatusDropdown ─────────────────────────────────────────────────────────────
const StatusDropdown = ({ status, onChange, open, setOpen, locked = false, disabledOptions = [], dropUp = false }) => {
  const ref = useRef(null);
  const current = STATUS_COLORS[status] || { bg: color.wine400, color: ink };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setOpen]);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <span style={{
        background: current.bg, color: current.color, borderRadius: radius.pill,
        padding: "4px 13px", fontFamily: font.ui, fontSize: "0.78rem",
        fontWeight: 500, userSelect: "none", whiteSpace: "nowrap",
      }}>
        {status}
      </span>
      {!locked && (
        <div
          onClick={() => setOpen(v => !v)}
          style={{
            background: color.wine800, border: `1px solid ${line}`, borderRadius: "8px", width: "26px", height: "26px",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          <svg width="10" height="7" viewBox="0 0 10 7" fill={inkMuted} stroke="none">
            <polygon points="0,0 10,0 5,7"/>
          </svg>
        </div>
      )}
      {open && !locked && (
        <div style={{
          position: "absolute", ...(dropUp ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }), right: 0,
          background: surface, border: `1px solid ${line}`, borderRadius: radius.card, padding: "8px",
          zIndex: 9999, display: "flex", flexDirection: "column", gap: "4px",
          minWidth: "130px", boxShadow: shadow.panel,
        }}>
          {STATUS_OPTIONS.map(opt => {
            const sc = STATUS_COLORS[opt];
            const isActive = opt === status;
            // Unavailable options are dimmed; the current status keeps its
            // coloured pill. Neither one can be clicked.
            const isBlocked  = !isActive && disabledOptions.includes(opt);
            const isDisabled = isActive || isBlocked;
            return (
              <div
                key={opt}
                onClick={() => { if (isDisabled) return; onChange(opt); setOpen(false); }}
                style={{
                  background: isActive ? sc.bg : "transparent",
                  color: isActive ? sc.color : inkBody,
                  borderRadius: radius.pill, padding: "5px 12px",
                  fontFamily: font.ui, fontSize: "0.78rem",
                  fontWeight: 500, cursor: isBlocked ? "not-allowed" : isActive ? "default" : "pointer", textAlign: "center",
                  whiteSpace: "nowrap", transition: `background 140ms ${ease}, color 140ms ${ease}`, userSelect: "none",
                  opacity: isBlocked ? 0.45 : 1,
                }}
                onMouseEnter={e => { if (isDisabled) return; e.currentTarget.style.background = sc.bg; e.currentTarget.style.color = sc.color; }}
                onMouseLeave={e => {
                  if (isDisabled) return;
                  e.currentTarget.style.background = isActive ? sc.bg : "transparent";
                  e.currentTarget.style.color = isActive ? sc.color : inkBody;
                }}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AttachedFileChip = ({ file }) => {
  // Images (PNG/JPG/etc.) get the img icon; anything else (PDF) gets the pdf icon.
  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(file.name || "") || /^image\//i.test(file.type || "");
  const [downloading, setDownloading] = useState(false);

  // NOTE: We deliberately avoid Cloudinary's `fl_attachment:<filename>` URL
  // transformation here. It's fragile — filenames with spaces or certain
  // special characters (even after encodeURIComponent) can make Cloudinary
  // return an HTTP 400 on the transformed URL. Fetching the raw file and
  // triggering a client-side Blob download works regardless of filename
  // content and doesn't depend on Cloudinary's transformation parsing.
  const handleDownload = async () => {
    if (!file.url || downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(file.url);
      if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (err) {
      console.error("Download failed, opening file in a new tab instead:", err);
      window.open(file.url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      onClick={handleDownload}
      title={downloading ? "Downloading…" : `Download ${file.name}`}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "4px", cursor: downloading ? "wait" : "pointer", opacity: downloading ? 0.6 : 1, userSelect: "none" }}
    >
      <div style={{ position: "relative", width: "68px", height: "78px" }}>
        <img src={isImage ? imgIcon : pdfIcon} alt={isImage ? "Image" : "PDF"} style={{ position: "absolute", top: 0, left: 0, width: "68px", height: "78px", objectFit: "contain", zIndex: 1 }} />
        <img src={downloadIcon} alt="Download" style={{ position: "absolute", top: "-6px", right: "-6px", width: "24px", height: "24px", objectFit: "contain", zIndex: 2 }} />
      </div>
      <span style={{ fontFamily: font.ui, fontSize: "0.7rem", color: inkMuted, textAlign: "center", wordBreak: "break-all", maxWidth: "80px", lineHeight: 1.3, marginTop: "4px" }}>
        {downloading ? "Downloading…" : file.name}
      </span>
    </div>
  );
};

// ── Status-saved confirmation modal ─────────────────────────────────────────────
const StatusSavedModal = ({ onClose }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2100, padding: "16px" }}>
    <div style={{ background: color.white, border: `1px solid ${line}`, borderRadius: radius.panel, maxWidth: "320px", width: "90%", padding: "30px 22px 24px", boxShadow: shadow.panel, textAlign: "center" }}>
      <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "rgba(90,117,96,0.14)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: "1.6rem", color: color.success }}>
        ✓
      </div>
      <p style={{ fontFamily: font.ui, fontSize: "1.1rem", fontWeight: 600, color: ink, margin: "0 0 6px" }}>Status saved successfully!</p>
      <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, margin: "0 0 20px" }}>
        The applicant has been notified of this update.
      </p>
      <button onClick={onClose} style={{ padding: "9px 30px", borderRadius: radius.pill, background: panel, color: onPanel, border: "none", fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
        OK
      </button>
    </div>
  </div>
);

// ── Alert (used by the report flow for inline validation) ─────────────────────
const AlertModal = ({ message, onClose }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3100, padding: "16px" }}>
    <div style={{ background: surface, borderRadius: radius.card, width: "100%", maxWidth: "340px", overflow: "hidden", textAlign: "center", border: `1px solid ${line}`, boxShadow: shadow.panel }}>
      <div style={{ padding: "22px 22px 16px" }}>
        <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, margin: 0 }}>{message}</p>
      </div>
      <button onClick={onClose} style={{ width: "100%", padding: "13px", border: "none", borderTop: `1px solid ${line}`, background: surface, color: ink, fontFamily: font.ui, ...type.control, cursor: "pointer" }}>OK</button>
    </div>
  </div>
);

// ── Report sent confirmation ──────────────────────────────────────────────────
const ReportSentModal = ({ onClose }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: "16px" }}>
    <div style={{ background: color.white, border: `1px solid ${line}`, borderRadius: radius.panel, maxWidth: "340px", width: "90%", padding: "30px 22px 24px", boxShadow: shadow.panel, textAlign: "center" }}>
      <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "rgba(90,117,96,0.14)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: "1.6rem", color: color.success }}>
        ✓
      </div>
      <p style={{ fontFamily: font.ui, fontSize: "1.1rem", fontWeight: 600, color: ink, margin: "0 0 6px" }}>Report sent</p>
      <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, margin: "0 0 20px" }}>
        The review team will look into this. The applicant isn't told who filed the report.
      </p>
      <button onClick={onClose} style={{ padding: "9px 30px", borderRadius: radius.pill, background: panel, color: onPanel, border: "none", fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
        OK
      </button>
    </div>
  </div>
);

// ── Report Applicant Modal ────────────────────────────────────────────────────
// Three-step flow kept deliberately identical to the student-side ReportModal in
// StudentFindCompanyScreen: pick a concern → read what it covers → describe it
// and attach evidence.
const ReportApplicantModal = ({ applicant, reporter, onClose, onSubmitted }) => {
  const [step, setStep]                 = useState(1);
  const [selected, setSelected]         = useState(null);
  const [description, setDescription]   = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]); // [{ name, type, file (raw) }]
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState("");
  const [alertMsg, setAlertMsg]         = useState("");
  const fileRef = useRef();

  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (picked.some(f => !["image/png", "application/pdf"].includes(f.type))) { setAlertMsg("That file type isn't supported. Attach PNG or PDF files only."); return; }
    const MAX_TOTAL = 10 * 1024 * 1024; // 10MB combined
    const current = attachedFiles.reduce((sum, a) => sum + (a.file?.size || 0), 0);
    const added   = picked.reduce((sum, f) => sum + f.size, 0);
    if (current + added > MAX_TOTAL) { setAlertMsg("Your files can't add up to more than 10MB in total."); return; }
    setAttachedFiles(prev => [...prev, ...picked.map(file => ({ name: file.name, type: file.type, file }))]);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "ojtern_reports");
    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error("The file didn't upload. Try again.");
    const data = await res.json();
    return { url: data.secure_url, name: file.name, type: file.type };
  };

  const handleSubmit = async () => {
    // Last line of defence: even if this modal were opened some other way, a
    // company can only ever file against a student.
    if (!isReportableApplicant(applicant)) { setAlertMsg("Only student applicants can be reported from this screen."); return; }
    if (!description.trim()) { setAlertMsg("Add a description of what happened."); return; }
    if (attachedFiles.length === 0)       { setAlertMsg("Attach a file that supports your report."); return; }
    setSubmitting(true);
    setSubmitError("");
    try {
      const filesData = await Promise.all(attachedFiles.map(a => uploadToCloudinary(a.file)));

      const studentName = buildFullName(applicant);
      const reportDoc = {
        // The coordinator's Reports list reads company / companyId / subjectType /
        // subjectId / subjectName (see CoordinatorReportCompanyScreen) — a report
        // without them has no name, no scope and was never reaching that list.
        // For a student report, companyId + subjectId hold the STUDENT's uid,
        // same as a report filed from chat (CompanyMessagesScreen.handleReport).
        company:        studentName,
        companyId:      applicant.studentId || "",
        subjectType:    "student",
        subjectId:      applicant.studentId || "",
        subjectName:    studentName,
        // Kept so a coordinator can scope it by the student's department.
        college:        applicant.college || "",
        program:        applicant.program || "",
        // `applicant.id` is the applications document id, so the student's own
        // uid comes from `studentId` — the same distinction handleMessage makes.
        studentId:      applicant.studentId || "",
        applicationId:  applicant.id || "",
        reportedName:   studentName,
        reportedRole:   "student",
        concern:        selected?.label || "Others",
        description,
        attachedFile:   filesData[0] || null, // first file, kept for older readers
        attachedFiles:  filesData,
        reportedBy:     reporter?.uid || "",
        reporterId:     reporter?.uid || "",
        reporterName:   applicant.companyName || reporter?.companyName || reporter?.name || "Unknown",
        reporterRole:   "company",
        date:           new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        createdAt:      serverTimestamp(),
        status:         "pending",
      };

      await addDoc(collection(db, "reports"), reportDoc);
      onSubmitted?.({ ...reportDoc, attachedFile: filesData[0] || null, attachedFiles: filesData });
      onClose();
    } catch (err) {
      setSubmitError(err.message || "The report didn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cat = reportCategories.find(c => c.label === selected?.label);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2500, padding: "16px" }}>
      <div style={{ background: surface, borderRadius: radius.panel, width: "100%", maxWidth: "540px", maxHeight: "86vh", overflow: "hidden", display: "flex", flexDirection: "column", border: `1px solid ${line}`, boxShadow: shadow.panel }}>
        <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${line}` }}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontFamily: font.ui, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em", color: ink }}>Report this applicant</span>
            <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginTop: "2px" }}>Step {step} of 3</p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: "1.15rem", cursor: "pointer", color: inkMuted, lineHeight: 1 }}>✕</button>
        </div>

        {/* Progress hairline — three segments, one per step */}
        <div id="creport-progress" style={{ display: "flex", gap: "3px", padding: "0 24px", marginTop: "10px" }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ flex: 1, height: "3px", borderRadius: radius.pill, background: n <= step ? ink : line, transition: `background 260ms ${ease}` }} />
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 24px" }}>
          {step === 1 && (
            <div id="creport-concerns">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "12px" }}>What is the concern?</p>
              {reportCategories.map((c) => {
                const isOn = selected?.label === c.label;
                return (
                  <div key={c.label} onClick={() => setSelected(c)} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", cursor: "pointer", borderBottom: `1px solid ${lineSoft}` }}>
                    <div style={{ width: "20px", height: "20px", borderRadius: "50%", border: `1.5px solid ${isOn ? ink : color.wine400}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: isOn ? ink : surface, transition: `all 180ms ${ease}` }}>
                      {isOn && <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: color.white }} />}
                    </div>
                    <span style={{ fontFamily: font.ui, ...type.body, color: isOn ? ink : inkBody }}>{c.label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {step === 2 && cat && (
            <div id="creport-details">
              <p style={{ fontFamily: font.ui, fontSize: "1rem", fontWeight: 600, color: ink, marginBottom: "10px" }}>{cat.label}</p>
              <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, marginBottom: "16px", maxWidth: "62ch" }}>{cat.description}</p>
              {cat.details.length > 0 && (
                <>
                  <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "10px" }}>Common forms this takes</p>
                  <ul style={{ paddingLeft: "18px", margin: 0 }}>
                    {cat.details.map((d, i) => <li key={i} style={{ fontFamily: font.ui, ...type.helper, color: inkBody, marginBottom: "6px" }}>{d}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
          {step === 3 && (
            <>
              <div id="creport-describe">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "10px" }}>Describe what happened</p>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Include dates, names, and anything the review team should see."
                style={{ width: "100%", minHeight: "112px", border: `1px solid ${line}`, borderRadius: radius.card, padding: "12px 14px", outline: "none", fontFamily: font.ui, ...type.body, resize: "vertical", background: color.wine800, color: ink, marginBottom: "20px", boxSizing: "border-box" }}
              />
              </div>
              <div id="creport-evidence">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "4px" }}>Attach evidence</p>
              <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginBottom: "10px" }}>PNG or PDF, up to 10MB in total. You can attach more than one file.</p>
              <input ref={fileRef} type="file" accept=".png,.pdf" multiple style={{ display: "none" }} onChange={handleFile} />
              {attachedFiles.length > 0 && (
                <div style={{ maxHeight: "132px", overflowY: "auto", overflowX: "hidden", padding: "8px 8px 2px 0", display: "flex", flexWrap: "wrap", gap: "10px", alignContent: "flex-start", marginBottom: "10px" }}>
                  {attachedFiles.map((a, idx) => (
                    <div key={idx} style={{ position: "relative", width: "84px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <img src={/^image\//i.test(a.type || "") ? imgIcon : pdfIcon} alt="" style={{ width: "52px", height: "60px", objectFit: "contain", display: "block" }} />
                      <span title={a.name} style={{ fontFamily: font.ui, fontSize: "0.68rem", color: inkBody, textAlign: "center", wordBreak: "break-all", maxWidth: "84px", lineHeight: 1.3 }}>{a.name}</span>
                      <button onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))} aria-label={`Remove ${a.name}`} style={{ position: "absolute", top: "-6px", right: "6px", width: "20px", height: "20px", borderRadius: "50%", background: color.wine800, border: `1px solid ${line}`, cursor: "pointer", color: inkMuted, fontSize: "0.85rem", lineHeight: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => fileRef.current.click()} style={{ display: "flex", alignItems: "center", gap: "10px", background: color.wine800, border: `1px dashed ${color.wine400}`, borderRadius: radius.card, padding: "12px 18px", cursor: "pointer", fontFamily: font.ui, ...type.control, color: ink }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                {attachedFiles.length > 0 ? "Add more files" : "Choose files"}
              </button>
              </div>
            </>
          )}
        </div>

        <div style={{ background: panel, padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <div style={{ minWidth: 0 }}>
            {submitError
              ? <p style={{ fontFamily: font.ui, ...type.helper, color: "#E8A5A2", margin: 0 }}>{submitError}</p>
              : <p style={{ fontFamily: font.ui, ...type.helper, color: onPanelDim, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{buildFullName(applicant)}</p>}
          </div>
          <div id="creport-actions" style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                style={{ padding: "9px 18px", borderRadius: radius.pill, background: "transparent", color: onPanelDim, border: `1px solid ${color.onWineFaint}`, fontFamily: font.ui, ...type.control, cursor: "pointer" }}
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => { if (step === 1 && !selected) { setAlertMsg("Pick a concern to continue."); return; } setStep(step + 1); }}
                style={{ padding: "9px 22px", borderRadius: radius.pill, background: color.white, color: ink, border: "none", fontFamily: font.ui, ...type.control, cursor: "pointer" }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ padding: "9px 22px", borderRadius: radius.pill, background: color.white, color: ink, border: "none", fontFamily: font.ui, ...type.control, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? "Sending…" : "Send report"}
              </button>
            )}
          </div>
        </div>
      </div>
      {alertMsg && <AlertModal message={alertMsg} onClose={() => setAlertMsg("")} />}
    </div>
  );
};

// ── Status Description Popup ──────────────────────────────────────────────────
const StatusDescriptionPopup = ({ status, onClose, onSend }) => {
  const [description, setDescription] = useState("");
  const sc = STATUS_COLORS[status] || { bg: color.success, color: color.white };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: "16px" }}>
      <div className="ca-popup-inner">
        <div style={{ padding: "20px 28px 0 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <span id="cstatus-badge" style={{ display: "inline-block", background: sc.bg, color: sc.color, borderRadius: radius.pill, padding: "5px 18px", fontFamily: font.ui, fontSize: "0.85rem", fontWeight: 500 }}>
            {status}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ background: color.wine800, border: `1px solid ${line}`, borderRadius: "50%", width: "28px", height: "28px", color: ink, fontSize: "1rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ margin: "16px 28px 0", borderTop: `1px solid ${line}` }} />
        <div id="cstatus-message" className="ca-popup-body">
          <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "10px" }}>Write a message:</p>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Write your message to the applicant..."
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend(description);
                onClose();
              }
            }}
            style={{ width: "100%", minHeight: "90px", border: "none", outline: "none", background: "transparent", fontFamily: font.ui, ...type.body, color: inkBody, resize: "none", lineHeight: 1.6 }} />
        </div>
        <div className="ca-popup-footer">
          <div id="cstatus-footer" style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose}
            style={{ background: "transparent", border: `1px solid ${onPanel}`, borderRadius: radius.pill, padding: "9px 26px", color: onPanel, fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
            Close
          </button>
          <button onClick={() => { onSend(description); onClose(); }}
            style={{ background: color.goldTint, border: "none", borderRadius: radius.pill, padding: "9px 26px", color: onPanel, fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
            Send
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Personal Details Modal ─────────────────────────────────────────────────────
const PersonalDetailsModal = ({ applicant, standing, onClose, onStatusChange, onMessage, user, onSubViewChange }) => {
  // A blocked or archived student can't act on the application, so it's shown
  // as view-only rather than left looking like it can still move along.
  const studentStanding = standing || { isActive: true, canProceed: true, state: "active", message: "" };
  const locationChips = [applicant.region, applicant.province, applicant.city, applicant.barangay].filter(Boolean);
  const collegeChips  = [applicant.college, applicant.program, applicant.major].filter(Boolean);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showStatusSaved, setShowStatusSaved] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showReportSent, setShowReportSent] = useState(false);

  // Messaging only opens up once the company has actually started reviewing
  // the application — a still-Pending applicant can't be contacted yet.
  const canMessage = applicant.status !== "Pending";
  // Companies report students only — never coordinators.
  const canReport  = isReportableApplicant(applicant);

  // "detail" | "status" | "report" → Applicants screen → Dashboard's "?" tour.
  const modalSub = pendingStatus ? "status" : (showReport && canReport) ? "report" : "detail";
  useEffect(() => { onSubViewChange?.(modalSub); }, [modalSub]);

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.50)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "clamp(16px, 5vw, 24px)" }}>
        <div className="ca-modal-inner">

          {/* Header */}
          <div className="ca-modal-header">
            <h2 id="cappd-header" title="Student Information" style={{ fontFamily: font.ui, fontSize: "clamp(1.05rem, 4vw, 1.3rem)", fontWeight: 600, letterSpacing: "-0.01em", margin: 0, color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>Student Information</h2>
            <button onClick={onClose} aria-label="Close" style={{ background: color.wine800, border: `1px solid ${line}`, borderRadius: "50%", width: "28px", height: "28px", color: ink, fontSize: "1rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>

          {/* Body */}
          <div className="ca-modal-body">
            {/* Name row */}
            <div id="cappd-name" className="ca-name-row">
              <div><FieldLabel>First Name</FieldLabel><Chip label={applicant.firstName} /></div>
              <div><FieldLabel>Middle Initial</FieldLabel><Chip label={applicant.middleInitial || "—"} /></div>
              <div><FieldLabel>Last Name</FieldLabel><Chip label={applicant.lastName} /></div>
              {applicant.suffix && <div><FieldLabel>Suffix</FieldLabel><Chip label={applicant.suffix} /></div>}
            </div>

            <div id="cappd-sex" style={{ marginBottom: "14px" }}><FieldLabel>Sex</FieldLabel><Chip label={applicant.sex} /></div>

            <div id="cappd-location" style={{ marginBottom: "14px" }}>
              <FieldLabel>Location</FieldLabel>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {locationChips.map((c, i) => <Chip key={i} label={c} />)}
              </div>
            </div>

            <div id="cappd-college" style={{ marginBottom: "14px" }}>
              <FieldLabel>College / Program / Major</FieldLabel>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {collegeChips.map((c, i) => <Chip key={i} label={c} />)}
              </div>
            </div>

            <hr style={{ border: "none", borderTop: `1px solid ${line}`, margin: "16px 0" }} />

            {/* Contact row */}
            <div id="cappd-contact" className="ca-contact-row">
              <div><FieldLabel>Contact Information</FieldLabel><Chip label={applicant.contact} /></div>
              <div><FieldLabel>Email address</FieldLabel><Chip label={applicant.email} /></div>
            </div>

            {/* Application message */}
            <div id="cappd-message" style={{ marginBottom: "16px" }}>
              <FieldLabel>Application message</FieldLabel>
              <div className="app-msg-scroll" style={{ background: color.wine800, border: `1px solid ${line}`, borderRadius: radius.card, padding: "13px 18px", maxHeight: "110px", overflowY: "auto", position: "relative" }}>
                <p style={{ fontFamily: font.ui, ...type.body, color: inkBody, margin: 0, wordBreak: "break-word" }}>
                  {applicant.message}
                </p>
              </div>
            </div>

            <hr style={{ border: "none", borderTop: `1px solid ${line}`, margin: "16px 0" }} />

            {/* Attached files — own scroll area */}
            <div id="cappd-files">
              <FieldLabel>Attached File</FieldLabel>
              {applicant.attachedFiles && applicant.attachedFiles.length > 0
                ? <div className="ca-files-scroll">
                    {applicant.attachedFiles.map((file, idx) => <AttachedFileChip key={idx} file={file} />)}
                  </div>
                : <span style={{ fontFamily: font.ui, ...type.helper, color: inkFaint, fontStyle: "italic" }}>No file attached</span>
              }
            </div>
          </div>

          <StandingNotice standing={studentStanding} style={{ margin: "0 18px 10px" }} />

          {/* Footer — stays put, never scrolls with the body */}
          <div className="ca-modal-footer">
            <div id="cappd-status" style={{ display: "flex", alignItems: "center", gap: "10px", width: "fit-content", maxWidth: "100%" }}>
              <FieldLabel style={{ margin: 0 }}>Status:</FieldLabel>
              <StatusDropdown
                status={applicant.status}
                onChange={(next) => { if (next !== applicant.status) setPendingStatus(next); }}
                open={dropdownOpen}
                setOpen={setDropdownOpen}
                locked={LOCKED_STATUSES.includes(applicant.status) || !studentStanding.canProceed}
                disabledOptions={getDisabledStatusOptions(applicant.status)}
                dropUp
              />
            </div>

            <div id="cappd-actions" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "100%" }}>
              <button
                onClick={() => { if (canMessage) onMessage(applicant); }}
                disabled={!canMessage}
                aria-disabled={!canMessage}
                title={canMessage ? "" : "Move this applicant to In Review before sending a message."}
                style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  background: panel, border: "none", borderRadius: radius.pill,
                  padding: "9px 18px", cursor: canMessage ? "pointer" : "not-allowed",
                  color: onPanel, opacity: canMessage ? 1 : 0.45,
                  fontFamily: font.ui, ...type.control,
                  transition: `background 220ms ${ease}`,
                }}
                onMouseEnter={e => { if (canMessage) e.currentTarget.style.background = panelDeep; }}
                onMouseLeave={e => { if (canMessage) e.currentTarget.style.background = panel; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Message
              </button>

              {canReport && (
                <button
                  onClick={() => setShowReport(true)}
                  title="Report this applicant"
                  style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "transparent", border: `1px solid ${line}`, borderRadius: radius.pill,
                    padding: "8px 16px", cursor: "pointer", color: inkBody,
                    fontFamily: font.ui, ...type.control,
                    transition: `background 220ms ${ease}`,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = color.wine800)}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <img src={reportIcon} alt="" style={{ width: "16px", height: "16px", objectFit: "contain", opacity: 0.75 }} />
                  Report
                </button>
              )}
            </div>

            {(applicant.status === "Accepted" || applicant.status === "Declined" || applicant.status === "Withdrawn" || !canMessage) && (
              <div className="ca-footer-note">
                {(applicant.status === "Accepted" || applicant.status === "Declined") && (
                  <span style={{ fontFamily: font.ui, ...type.helper, color: inkFaint, fontStyle: "italic" }}>
                    This applicant has been {applicant.status.toLowerCase()}. Status is locked and can no longer be changed.
                  </span>
                )}
                {applicant.status === "Accepted" && (
                  <span style={{ fontFamily: font.ui, ...type.helper, color: applicant.placementConfirmed ? "#2a7a2a" : inkFaint, fontStyle: applicant.placementConfirmed ? "normal" : "italic", fontWeight: applicant.placementConfirmed ? 600 : 400 }}>
                    {applicant.placementConfirmed
                      ? `✓ Confirmed by the student as their OJT placement${applicant.placementConfirmedAt?.seconds ? ` on ${new Date(applicant.placementConfirmedAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""}.`
                      : "Waiting for the student to confirm this as their OJT placement."}
                  </span>
                )}
                {applicant.status === "Withdrawn" && (
                  <span style={{ fontFamily: font.ui, ...type.helper, color: inkFaint, fontStyle: "italic" }}>
                    The student withdrew this application
                    {applicant.withdrawnAt?.seconds ? ` on ${new Date(applicant.withdrawnAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""}
                    {applicant.statusBeforeWithdrawal ? ` (it was ${applicant.statusBeforeWithdrawal})` : ""}.
                    {applicant.withdrawalReason ? ` Reason: ${[applicant.withdrawalReason, applicant.withdrawalReasonDetails].filter(Boolean).join(" — ")}.` : ""}
                    {" "}It's kept on record and can no longer be changed.
                    {applicant.statusBeforeWithdrawal === "Accepted" ? " The slot has been returned to your post." : ""}
                  </span>
                )}
                {!canMessage && (
                  <span style={{ fontFamily: font.ui, ...type.helper, color: inkFaint, fontStyle: "italic" }}>
                    Set the status to In Review before you can message this applicant.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {pendingStatus && (
        <StatusDescriptionPopup
          status={pendingStatus}
          onClose={() => setPendingStatus(null)}
          onSend={(description) => {
            onStatusChange(applicant.id, pendingStatus, description);
            setPendingStatus(null);
            setShowStatusSaved(true);
          }}
        />
      )}

      {showStatusSaved && (
        <StatusSavedModal onClose={() => setShowStatusSaved(false)} />
      )}

      {showReport && canReport && (
        <ReportApplicantModal
          applicant={applicant}
          reporter={user}
          onClose={() => setShowReport(false)}
          onSubmitted={() => setShowReportSent(true)}
        />
      )}

      {showReportSent && (
        <ReportSentModal onClose={() => setShowReportSent(false)} />
      )}
    </>
  );
};

// ── Filter Panel ──────────────────────────────────────────────────────────────
const FilterPanel = ({
  filters, setFilters,
  filterRef,
}) => {
  const sexOptions = ["Male", "Female"];

  const colleges = Object.keys(COLLEGE_DATA);

  const programs = filters.college
    ? Object.keys(COLLEGE_DATA[filters.college]?.programs || {})
    : [];

  const specializations = (filters.college && filters.program)
    ? (COLLEGE_DATA[filters.college]?.programs?.[filters.program]?.specializations || [])
    : [];

  const clearAll = () => {
    setFilters({ sex: "", college: "", program: "", specialization: "", status: "" });
  };

  const toggleSex    = (val) => setFilters(prev => ({ ...prev, sex: prev.sex === val ? "" : val }));
  const toggleStatus = (val) => setFilters(prev => ({ ...prev, status: prev.status === val ? "" : val }));

  const groupLabel = { fontFamily: font.ui, ...type.label, color: ink };
  const emptyNote  = { fontFamily: font.ui, ...type.helper, color: inkFaint, fontStyle: "italic" };

  return (
    <div
      ref={filterRef}
      style={{
        position: "absolute", top: "48px", right: 0, width: "266px",
        background: surface, border: `1px solid ${line}`, borderRadius: radius.card,
        boxShadow: shadow.panel, zIndex: 100, overflow: "hidden",
        fontFamily: font.ui,
      }}
    >
      {/* Sex */}
      <div style={{ padding: "12px 14px 6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space.sm }}>
          <p style={groupLabel}>Sex</p>
          <button onClick={clearAll} style={{ background: "none", border: "none", fontFamily: font.ui, ...type.helper, color: inkMuted, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Clear all</button>
        </div>
        {sexOptions.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {sexOptions.map(s => (
              <span key={s} onClick={() => toggleSex(s)} style={chip(filters.sex === s)}>{s}</span>
            ))}
          </div>
        ) : (
          <p style={emptyNote}>No options available.</p>
        )}
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: "10px 0" }} />

      {/* College / Program / Specialization */}
      <div style={{ padding: "0 14px 10px" }}>
        <p style={{ ...groupLabel, marginBottom: space.sm }}>College</p>
        {colleges.length > 0 ? (
          <div style={{ maxHeight: "160px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
            {colleges.map(col => (
              <div key={col}
                onClick={() => setFilters(prev => ({ ...prev, college: prev.college === col ? "" : col, program: "", specialization: "" }))}
                style={{ padding: "7px 11px", borderRadius: "10px", fontFamily: font.ui, ...type.helper, color: inkBody, cursor: "pointer", background: filters.college === col ? color.wine700 : color.wine800, border: `1px solid ${line}`, transition: `background 160ms ${ease}` }}
                onMouseEnter={e => e.currentTarget.style.background = color.wine700}
                onMouseLeave={e => e.currentTarget.style.background = filters.college === col ? color.wine700 : color.wine800}
              >{col}</div>
            ))}
          </div>
        ) : (
          <p style={emptyNote}>No options available.</p>
        )}

        {filters.college && (
          <>
            <p style={{ ...groupLabel, margin: "10px 0 6px" }}>Program</p>
            {programs.length > 0 ? (
              <div style={{ maxHeight: "120px", overflowY: "auto", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {programs.map(prog => (
                  <span key={prog} onClick={() => setFilters(prev => ({ ...prev, program: prev.program === prog ? "" : prog, specialization: "" }))} style={chip(filters.program === prog)}>
                    {prog}
                  </span>
                ))}
              </div>
            ) : (
              <p style={emptyNote}>No options available.</p>
            )}
          </>
        )}

        {filters.program && (
          <>
            <p style={{ ...groupLabel, margin: "10px 0 6px" }}>Specialization</p>
            {specializations.length > 0 ? (
              <div style={{ maxHeight: "120px", overflowY: "auto", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {specializations.map(spec => (
                  <span key={spec} onClick={() => setFilters(prev => ({ ...prev, specialization: prev.specialization === spec ? "" : spec }))} style={chip(filters.specialization === spec)}>
                    {spec}
                  </span>
                ))}
              </div>
            ) : (
              <p style={emptyNote}>No specializations available.</p>
            )}
          </>
        )}
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: 0 }} />

      {/* Status */}
      <div style={{ padding: "10px 14px 12px" }}>
        <p style={{ ...groupLabel, marginBottom: space.sm }}>Status</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {STATUS_OPTIONS.map(s => (
            <span key={s} onClick={() => toggleStatus(s)} style={chip(filters.status === s)}>{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main Screen ────────────────────────────────────────────────────────────────
// Live standing of each applicant's student account. A blocked student is a
// disciplinary matter; an archived one has simply stopped being a current
// student (a graduated batch, say) — different wording, same effect on the
// application: it can't go anywhere, so it's kept view-only.
const useStudentStandings = (studentIds) => {
  const [standings, setStandings] = useState({});
  const key = [...new Set(studentIds.filter(Boolean))].sort().join(",");

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    if (ids.length === 0) { setStandings({}); return; }
    const unsubs = ids.map(id => onSnapshot(
      doc(db, "students", id),
      snap => setStandings(prev => ({ ...prev, [id]: standingOf(snap.exists() ? snap.data() : null, "student") })),
      err => console.error("Failed to watch student standing:", err)
    ));
    return () => unsubs.forEach(u => u());
  }, [key]);

  return standings;
};

const StandingNotice = ({ standing, style = {} }) => {
  if (!standing || standing.isActive) return null;
  const tone = STANDING_TONE[standing.state] || STANDING_TONE.suspended;
  return (
    <div role="status" style={{
      background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: "12px",
      padding: "10px 14px", margin: "0 0 10px", ...style,
    }}>
      <p style={{ fontFamily: font.ui, fontSize: "0.76rem", color: tone.text, margin: 0, lineHeight: 1.6 }}>
        {standing.message}
      </p>
    </div>
  );
};

// ── Pagination: 10 rows per page ──────────────────────────────────────────────
const PAGE_SIZE = 10;
// The header (title, search, filters) is fixed; only the list under it scrolls, with its own scrollbar.
// The list box reaches into the screen padding so its scrollbar sits at the edge.
const PIN_PAD = "clamp(16px, 4vw, 28px)";
const PIN_PAD_X = "clamp(16px, 4vw, 32px)";
const fixedHead = { flexShrink: 0, position: "relative", zIndex: 30 };
const listScroll = { flex: "1 1 auto", minHeight: 0, overflowY: "auto", margin: `0 calc(-1 * ${PIN_PAD_X}) calc(-1 * ${PIN_PAD})`, padding: `0 ${PIN_PAD_X} ${PIN_PAD}` };
// Next/previous page: the list goes back to its start.
const scrollListToTop = (scroller) => { if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" }); };
// « ‹ 1 2 3 … 9 › » — current-page badge + page buttons in one rounded bar.
// Always sits on the RIGHT, on desktop and on mobile (the "Showing x–y of z" note follows it to the right on small screens).
const PaginationBar = ({ page: current, totalPages, onPage, from, to, total }) => {
  const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
  const items = totalPages <= 7 ? range(1, totalPages)
    : current <= 4 ? [...range(1, 5), "gap-end", totalPages]
    : current >= totalPages - 3 ? [1, "gap-start", ...range(totalPages - 4, totalPages)]
    : [1, "gap-start", current - 1, current, current + 1, "gap-end", totalPages];
  const btn = (on, disabled) => ({
    minWidth: "36px", height: "36px", padding: "0 10px", borderRadius: "10px",
    border: `1.5px solid ${on ? color.blush50 : color.wine700}`, background: on ? color.blush50 : color.white, color: on ? color.white : color.ink,
    fontFamily: font.ui, fontSize: "0.82rem", fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1,
  });
  return (
    <div className="pg-bar" style={{ display: "flex", alignItems: "center", justifyContent: total != null ? "space-between" : "flex-end", gap: "12px", padding: "20px 0 4px" }}>
      <style>{`
        @media (max-width: 640px) {
          .pg-bar { flex-direction: column-reverse; align-items: flex-end !important; justify-content: flex-start !important; gap: 8px !important; }
          .pg-info { text-align: right; }
        }
      `}</style>
      {total != null && <span className="pg-info" style={{ fontFamily: font.ui, fontSize: "0.8rem", color: color.inkMuted }}>Showing {from}–{to} of {total}</span>}
      <nav aria-label="Pagination" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "stretch", maxWidth: "100%", background: color.white, border: `1px solid ${color.wine700}`, borderRadius: "16px", boxShadow: "0 4px 16px rgba(20,20,20,0.06)", overflow: "hidden" }}>
        <div title="Current page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "52px", padding: "0 14px", background: color.wine800, borderRight: `1px solid ${color.wine700}`, fontFamily: font.ui, fontSize: "0.9rem", fontWeight: 700, color: color.ink }}>
          {String(current).padStart(2, "0")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 12px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button type="button" aria-label="First page" title="First page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(1)}>«</button>
          <button type="button" aria-label="Previous page" title="Previous page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(current - 1)}>‹</button>
          {items.map((it) => (typeof it === "number"
            ? <button key={it} type="button" style={btn(it === current, false)} aria-current={it === current ? "page" : undefined} onClick={() => onPage(it)}>{it}</button>
            : <span key={it} style={{ color: color.inkMuted, padding: "0 4px" }}>…</span>))}
          <button type="button" aria-label="Next page" title="Next page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(current + 1)}>›</button>
          <button type="button" aria-label="Last page" title="Last page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(totalPages)}>»</button>
        </div>
      </nav>
    </div>
  );
};

const CompanyApplicantsScreen = ({ embedded = false, onNavigateToMessages, user, openApplicantId, onApplicantOpened, initialStatusFilter, onStatusFilterApplied , onViewChange }) => {
  const [applicants, setApplicants] = useState([]);

  // Fetch applications for this company from Firestore
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "applications"),
      where("companyId", "==", user.uid)
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setApplicants(docs);
    }, err => console.error("Applicants fetch error:", err));
    return () => unsub();
  }, [user?.uid]);

  // Give the slot back when an ACCEPTED student withdraws. The student marks
  // the application `slotReturned: false`; the company owns the post, so this
  // screen performs the post write. A transaction re-checks the flag first, so
  // the slot is returned exactly once even with two tabs open.
  useEffect(() => {
    const pendingReturns = applicants.filter(a =>
      a.status === "Withdrawn" && a.statusBeforeWithdrawal === "Accepted" && a.slotReturned === false && a.postId
    );
    pendingReturns.forEach(a => {
      runTransaction(db, async (tx) => {
        const appRef  = doc(db, "applications", a.id);
        const postRef = doc(db, "ojt_posts", a.postId);
        const [appSnap, postSnap] = await Promise.all([tx.get(appRef), tx.get(postRef)]);
        if (!appSnap.exists() || appSnap.data().slotReturned !== false) return;
        if (postSnap.exists()) {
          tx.update(postRef, { slot: (postSnap.data().slot ?? 0) + 1 });
        }
        tx.update(appRef, { slotReturned: true, slotReturnedAt: serverTimestamp() });
      }).catch(err => console.error("Failed to return slot for withdrawn applicant:", err));
    });
  }, [applicants]);
  const [search, setSearch]                     = useState("");
  const [curPage, setCurPage]                   = useState(1);
  // Next/previous page: the list goes back to its start.
  const scrollRef = useRef(null);
  const goToPage = (n) => { setCurPage(n); requestAnimationFrame(() => scrollListToTop(scrollRef.current)); };
  const [viewingApplicant, setViewingApplicant] = useState(null);
  const [detailSub, setDetailSub]               = useState("detail");
  // "list" | "detail" | "status" | "report" → Dashboard's "?" help button
  // and first-visit auto-tour.
  const subView = viewingApplicant ? detailSub : "list";
  useEffect(() => { onViewChange?.(subView); }, [subView]);
  useEffect(() => () => onViewChange?.("list"), []);
  useEffect(() => { if (!viewingApplicant) setDetailSub("detail"); }, [viewingApplicant]);
  const [showFilter, setShowFilter]             = useState(false);
  const [filters, setFilters]                   = useState({ sex: "", college: "", program: "", specialization: "", status: "" });
  const [activeNav, setActiveNav]               = useState("Applicants");

  // Auto-open personal details when navigated here with a specific applicant
  // (e.g. clicking a "Recent Applicant" on the dashboard).
  useEffect(() => {
    if (!openApplicantId) return;
    const target = applicants.find(a => a.id === openApplicantId);
    if (target) {
      setViewingApplicant(target);
      onApplicantOpened?.();
    }
  }, [openApplicantId, applicants, onApplicantOpened]);

  const filterRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Set initial status filter when navigated from dashboard
  useEffect(() => {
    if (initialStatusFilter) {
      setFilters(p => ({ ...p, status: initialStatusFilter }));
      onStatusFilterApplied?.();
    }
  }, [initialStatusFilter, onStatusFilterApplied]);

  const navItems = [
    { key: "dashboard",      label: "Dashboard",       icon: dashboardIcon },
    { key: "postojt",        label: "Post OJT",        icon: postOJTIcon },
    { key: "applicants",     label: "Applicants",      icon: applicantsIcon },
    { key: "messages",       label: "Messages",        icon: messagesIcon },
    { key: "accountprofile", label: "Account Profile", icon: accountProfileIcon },
    { key: "about",          label: "About",           icon: aboutIcon },
  ];

  const hasFilter = filters.sex || filters.college || filters.program || filters.specialization || filters.status;

  const filtered = applicants.filter(a => {
    const fullName = `${a.firstName} ${a.lastName}`.toLowerCase();
    const q = search.toLowerCase();
    const matchSearch   = fullName.includes(q) || (a.college || "").toLowerCase().includes(q) || (a.program || "").toLowerCase().includes(q);
    const matchSex      = !filters.sex || a.sex === filters.sex;
    const matchCollege  = !filters.college || a.college === filters.college;
    const matchProgram  = !filters.program || a.program === filters.program;
    const matchSpec     = !filters.specialization || a.major === filters.specialization;
    const matchStatus   = !filters.status || a.status === filters.status;
    return matchSearch && matchSex && matchCollege && matchProgram && matchSpec && matchStatus;
  });

  useEffect(() => { setCurPage(1); }, [search, filters]);
  // ── Pagination (10 per page) ──
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(curPage, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showingFrom = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min(safePage * PAGE_SIZE, filtered.length);

  // Human-readable phrasing per status, used in the notification sent to the student.
  const STATUS_NOTIF_TEXT = {
    "In Review":     "is now in review",
    "To Interview":  "has moved to the interview stage",
    "Accepted":      "has been accepted",
    "Declined":      "has been declined",
  };

  // Standing of every applicant's student account, watched live.
  const studentStandings = useStudentStandings(applicants.map(a => a.studentId));
  const standingFor = (applicant) =>
    studentStandings[applicant?.studentId] || { isActive: true, canProceed: true, state: "active", message: "" };

  const handleStatusChange = async (id, newStatus, description = "") => {
    const current = applicants.find(a => a.id === id);
    // No actual change — nothing to persist, and importantly nothing that
    // should trigger another status-update email (the backend Cloud
    // Function guards this too via oldData.status !== newData.status, but
    // catching it here avoids an unnecessary Firestore write in the first
    // place).
    if (current?.status === newStatus) {
      return;
    }
    // A blocked or archived student can't log in, so the application can't go
    // anywhere: it stays on record as view-only rather than being moved along.
    const standing = standingFor(current);
    if (!standing.canProceed) {
      return;
    }
    // Accepted/Declined/Withdrawn are final — no further changes.
    if (LOCKED_STATUSES.includes(current?.status)) {
      console.warn(`Blocked status change: applicant already ${current.status}.`);
      return;
    }
    // Block backward moves (e.g. In Review -> Pending) and premature final
    // decisions (e.g. Pending -> Accepted). Mirrors the greyed-out options in
    // the dropdown so the rule holds even if the UI is bypassed.
    if (getDisabledStatusOptions(current?.status).includes(newStatus)) {
      console.warn(`Blocked status change: ${current?.status} -> ${newStatus} is not an allowed transition.`);
      return;
    }
    const note = (description || "").trim();
    // Optimistic local update
    setApplicants(prev => prev.map(a => a.id === id ? { ...a, status: newStatus, statusNote: note } : a));
    if (viewingApplicant?.id === id) setViewingApplicant(prev => ({ ...prev, status: newStatus, statusNote: note }));
    // Persist to Firestore
    try {
      await updateDoc(doc(db, "applications", id), {
        status: newStatus,
        statusNote: note,
        // The coordinator's placement review starts once the STUDENT confirms
        // this as their OJT placement (StudentApplicationScreen → Confirm),
        // since a student may be accepted by more than one company.
        ...(newStatus === "Accepted" ? { acceptedAt: serverTimestamp() } : {}),
      });

      if (newStatus === "Accepted") {
        // Best-effort: the acceptance above is already saved.
        const studentName = [current?.firstName, current?.lastName].filter(Boolean).join(" ") || current?.studentName || "A student";
        addDoc(collection(db, "notifications"), {
          recipientRole:      "coordinator",
          department:         current?.college || "",
          program:            current?.program || "",
          type:               "application_accepted",
          applicantStudentId: current?.studentId || "",   // NOT studentId — keeps it out of the student's bell
          studentName,
          companyId:          current?.companyId || "",
          companyName:        current?.companyName || "",
          applicationId:      id,
          message:            `${studentName} was accepted by ${current?.companyName || "a company"}. Waiting for the student to confirm it as their OJT placement.`,
          readBy:             [],
          createdAt:          serverTimestamp(),
        }).catch(err => console.error("Accepted, but notifying the coordinator failed:", err));
      }

      // When a student is accepted, the specific post they applied to has
      // one fewer open slot. Uses a transaction (not a plain increment) so
      // the count never goes below 0 even if multiple accepts land at once.
      if (newStatus === "Accepted" && current?.postId) {
        try {
          await runTransaction(db, async (tx) => {
            const postRef = doc(db, "ojt_posts", current.postId);
            const postSnap = await tx.get(postRef);
            if (postSnap.exists()) {
              const currentSlot = postSnap.data().slot ?? 0;
              tx.update(postRef, { slot: Math.max(0, currentSlot - 1) });
            }
          });
        } catch (slotErr) {
          console.error("Failed to update post slot count:", slotErr);
        }
      }

      // Notify the student about this status change
      if (current?.studentId) {
        const statusText = STATUS_NOTIF_TEXT[newStatus] || `is now ${newStatus.toLowerCase()}`;
        const baseMessage = `Your application on ${current.companyName || "the company"} ${statusText}.`
          + (newStatus === "Accepted" ? " If this is where you want to have your OJT, confirm it in Recent Applications." : "");
        await addDoc(collection(db, "notifications"), {
          studentId:     current.studentId,
          message:       note ? `${baseMessage} Message from the company: "${note}"` : baseMessage,
          companyNote:   note,
          type:          "application_status",
          applicationId: id,
          companyName:   current.companyName || "",
          status:        newStatus,
          read:          false,
          createdAt:     serverTimestamp(),
        });
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleMessage = (applicant) => {
    setViewingApplicant(null);
    if (onNavigateToMessages) {
      onNavigateToMessages({
        id: applicant.studentId,        // ✅ real student uid, not applicant.id
        name: buildFullName(applicant), // ✅ includes middle initial + suffix
      });
    }
  };

  const contentArea = (
    <div className="ca-list-wrapper">
      <div style={fixedHead}>

      {/* Header bar — same floating dark panel as Students List */}
      <div className="ca-search-bar">
        <div style={{ minWidth: 0 }}>
          <span style={{ fontFamily: font.ui, fontSize: "clamp(1.1rem, 3.5vw, 1.375rem)", fontWeight: 600, letterSpacing: "-0.01em", color: onPanel }}>Applicants</span>
          <p style={{ fontFamily: font.ui, ...type.helper, color: onPanelDim, marginTop: "2px" }}>
            {filtered.length} of {applicants.length} applicant{applicants.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0, gap: "10px" }}>
          {/* Search */}
          <div id="capp-search-bar" style={{ display: "flex", alignItems: "center", gap: space.sm, background: color.white, borderRadius: radius.pill, padding: "9px 16px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={inkMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search Application"
              className="ca-search-input"
              style={{ border: "none", background: "transparent", outline: "none", color: ink, fontFamily: font.ui, ...type.control }}
            />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Clear search" style={{ background: "none", border: "none", color: inkMuted, cursor: "pointer", fontSize: "0.9rem", padding: 0, lineHeight: 1 }}>✕</button>
            )}
          </div>

          {/* Filter button */}
          <div style={{ position: "relative" }}>
            <div
              onClick={() => setShowFilter(v => !v)}
              title="Filters"
              style={{ width: "40px", height: "40px", background: hasFilter ? color.goldTint : color.white, borderRadius: radius.pill, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: hasFilter ? `1px solid ${color.onWineFaint}` : "none" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={hasFilter ? onPanel : inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
            </div>
            {showFilter && (
              <FilterPanel
                filters={filters} setFilters={setFilters}
                filterRef={filterRef}
              />
            )}
          </div>
        </div>
      </div>

      {/* Status chips */}
      <div id="capp-status-chips" style={{ display: "flex", gap: space.sm, alignItems: "center", flexWrap: "wrap", marginBottom: space.md }}>
        {["All", "Accepted", "Declined", "Pending", "In Review", "To Interview", "Withdrawn"].map((statusOption) => {
          const isActive = statusOption === "All" ? filters.status === "" : filters.status === statusOption;
          // Each option keeps the colour of the status it represents, so the
          // chips and the row badges read as the same language.
          const statusColor = statusOption === "All" ? ink : (STATUS_COLORS[statusOption]?.bg || color.wine400);
          const activeText  = statusOption === "All" ? color.white : (STATUS_COLORS[statusOption]?.color || ink);

          return (
            <button
              key={statusOption}
              onClick={() => setFilters(p => ({ ...p, status: statusOption === "All" ? "" : statusOption }))}
              style={{
                background: isActive ? statusColor : surface,
                color: isActive ? activeText : inkBody,
                border: isActive ? "none" : `1px solid ${line}`,
                borderRadius: radius.pill,
                padding: "7px 16px",
                fontFamily: font.ui,
                ...type.helper,
                fontWeight: isActive ? 500 : 400,
                cursor: "pointer",
                transition: `all 180ms ${ease}`,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = color.wine400; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = line; }}
            >
              {statusOption}
            </button>
          );
        })}
      </div>

      </div>

      <div ref={scrollRef} style={listScroll}>
      {/* Applicant list */}
      {filtered.length > 0 ? (
        <>
        <div id="capp-rows" className="ca-rows">
          {pageRows.map(applicant => {
            const fullName = buildFullName(applicant) !== "User" ? buildFullName(applicant) : "this applicant";
            const sc = STATUS_COLORS[applicant.status] || { bg: color.wine400, color: ink };
            return (
              <div
                key={applicant.id}
                className="ca-row"
                onClick={() => setViewingApplicant(applicant)}
              >
                <StudentAvatar size={42} />
                <div style={{ width: "1px", height: "30px", background: line, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: font.ui, ...type.label, color: ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {fullName}
                  </p>
                  <div className="ca-row-meta">
                    <span style={{ background: sc.bg, color: sc.color, borderRadius: radius.pill, padding: "2px 10px", fontFamily: font.ui, fontSize: "0.72rem", fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 }}>
                      {applicant.status}
                    </span>
                    {applicant.status === "Accepted" && applicant.placementConfirmed && (
                      <span style={{ background: "#EAF6EE", color: "#1f5e36", border: "1px solid #BFE3CB", borderRadius: radius.pill, padding: "1px 8px", fontFamily: font.ui, fontSize: "0.68rem", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                        ✓ Confirmed
                      </span>
                    )}
                    <span className="ca-row-program" style={{ color: color.wine400, fontSize: "0.7rem", flexShrink: 0 }}>·</span>
                    <span className="ca-row-program" style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {applicant.program}
                    </span>
                  </div>
                </div>
                <img src={viewIcon} alt="view" style={{ width: "32px", height: "32px", objectFit: "contain", flexShrink: 0, opacity: 0.7 }} />
              </div>
            );
          })}
        </div>
        <PaginationBar page={safePage} totalPages={totalPages} onPage={goToPage} from={showingFrom} to={showingTo} total={filtered.length} />
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "72px 24px", gap: space.xs, background: surface, border: `1px dashed ${color.wine400}`, borderRadius: radius.panel }}>
          <p style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, color: ink }}>
            {applicants.length === 0 ? "No applicants yet" : "No applicants match your search or filters"}
          </p>
          <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, maxWidth: "44ch" }}>
            {applicants.length === 0
              ? "Applicants will appear here once students apply to your OJT posts."
              : "Try a different name or ID, or clear a filter to widen the results."}
          </p>
        </div>
      )}
      </div>
    </div>
  );

  const overlays = (
    <>
      {viewingApplicant && (
        <PersonalDetailsModal
          applicant={viewingApplicant}
          standing={standingFor(viewingApplicant)}
          onClose={() => setViewingApplicant(null)}
          onStatusChange={handleStatusChange}
          onMessage={handleMessage}
          user={user}
          onSubViewChange={setDetailSub}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        <ResponsiveStyles />
        {contentArea}
        {overlays}
      </>
    );
  }

  return (
    <>
      <ResponsiveStyles />
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: page }}>

        {/* App header */}
        <div className="ca-app-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img src={logo} alt="OJTern" style={{ width: "42px", height: "42px", objectFit: "contain" }} />
            <span style={{ fontFamily: font.logo, fontSize: "clamp(1.1rem, 3vw, 1.4rem)", color: onPanel, letterSpacing: "0.03em" }}>OJTern</span>
          </div>
        </div>

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* Sidebar — hidden on mobile via CSS */}
          <div className="ca-sidebar">
            {navItems.map((item) => (
              <div
                key={item.key}
                onClick={() => setActiveNav(item.label)}
                style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px 20px", cursor: "pointer", borderBottom: `1px solid ${color.blush300}`, transition: `background 160ms ${ease}`, background: activeNav === item.label ? color.goldTint : "transparent" }}
              >
                <img src={item.icon} alt="" style={{ width: "26px", height: "26px", objectFit: "contain", flexShrink: 0, opacity: activeNav === item.label ? 1 : 0.4 }} />
                <span className="ca-nav-label" style={{ fontFamily: font.ui, fontSize: "0.95rem", fontWeight: activeNav === item.label ? 600 : 400, color: onPanel, opacity: activeNav === item.label ? 1 : 0.6 }}>{item.label}</span>
              </div>
            ))}
          </div>

          {contentArea}
        </div>

        {overlays}
      </div>
    </>
  );
};

export default CompanyApplicantsScreen;